import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft, Search, BookOpen, GraduationCap, ChevronRight, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';

const GLOSSARY_TERMS = [
  { term: "~50/50 Split", definition: "2026 power delivery: roughly 50% ICE, 50% MGU-K electric." },
  { term: "Active Aero (Active Aerodynamics)", definition: "Movable front/rear wing elements adjusting dynamically in high-speed zones for Corner/Straight modes." },
  { term: "Aero (Aerodynamics)", definition: "Airflow management for downforce and drag reduction." },
  { term: "Airbox", definition: "Engine intake above the driver's head, also serving as roll hoop protection." },
  { term: "Apex", definition: "Innermost point of a corner on the ideal racing line." },
  { term: "Axle Track (Track Width)", definition: "Distance between hub centers on the same axle." },
  { term: "Backmarker", definition: "Slower car lapped by leaders; must yield under blue flags." },
  { term: "Balance (Car Balance)", definition: "Front/rear grip bias (neutral, understeer, oversteer)." },
  { term: "Ballast", definition: "Extra weight to meet minimum and tune center of gravity." },
  { term: "Blistering", definition: "Tyre overheating causing surface bubbles/fragments." },
  { term: "Boost (Boost Button / Boost Mode)", definition: "Driver button for full ERS energy deployment anywhere." },
  { term: "Brake Balance (Brake Bias)", definition: "Adjustable front/rear braking force split." },
  { term: "Chassis (Monocoque)", definition: "Main structural tub/frame." },
  { term: "Chequered Flag", definition: "Signals session/race end." },
  { term: "Clean Air", definition: "Undisturbed airflow to a car (full downforce)." },
  { term: "Clutch", definition: "Driver-controlled for starts/gear changes." },
  { term: "Compounds", definition: "Tyre rubber types (C1–C5 dry slicks, intermediates, wets)." },
  { term: "Corner Mode", definition: "Active Aero high-downforce setup for turns." },
  { term: "Delta", definition: "Time difference to reference (lap delta, sector delta)." },
  { term: "Dirty Air", definition: "Turbulent wake reducing following car's downforce." },
  { term: "Downforce", definition: "Aerodynamic load increasing cornering grip." },
  { term: "Drag", definition: "Air resistance opposing forward motion." },
  { term: "DRS", definition: "Obsolete (pre-2026 overtaking aid via rear wing flap)." },
  { term: "Energy Recovery System (ERS)", definition: "Hybrid setup (MGU-K + Energy Store)." },
  { term: "Energy Store (ES)", definition: "Battery holding recovered/deployable energy." },
  { term: "Flat Floors", definition: "2026 underbody (no Venturi tunnels) for less downforce/dirty air." },
  { term: "Front Wing (FW)", definition: "Leading aero device, movable in Active Aero." },
  { term: "G-Force", definition: "Lateral/longitudinal forces on driver (e.g., braking/cornering)." },
  { term: "Graining", definition: "Tyre rubber tearing into rough chunks." },
  { term: "Gravel Trap", definition: "Run-off filled with gravel to decelerate off-track cars." },
  { term: "Hairpin", definition: "Tight 180-degree low-speed corner." },
  { term: "Hotlap", definition: "Single maximum-effort lap (qualifying-style)." },
  { term: "Internal Combustion Engine (ICE)", definition: "1.6L V6 turbo, ~50% power in 2026." },
  { term: "Kerb", definition: "Raised track edging for extra width (risks damage)." },
  { term: "Lift and Coast", definition: "Early throttle lift to save fuel/energy." },
  { term: "MGU-K (Motor Generator Unit - Kinetic)", definition: "~350kW 2026 unit for braking recovery/deployment." },
  { term: "Marbles", definition: "Loose rubber off racing line reducing grip." },
  { term: "Minimum Weight", definition: "770kg (car + driver) in 2026." },
  { term: "Overtake Mode", definition: "2026 passing aid: extra +0.5MJ energy when <1s behind at points." },
  { term: "Oversteer", definition: "Rear slides first in corner (loose handling)." },
  { term: "Parc Fermé", definition: "Post-qualifying sealed area (limited car changes)." },
  { term: "Pits (Pit Lane / Pit Stop)", definition: "Tyre changes, repairs; no refuelling." },
  { term: "Plank", definition: "Wooden floor strip checked for wear/ride height." },
  { term: "Pole Position", definition: "P1 grid spot from fastest quali lap." },
  { term: "Power Unit (PU)", definition: "Full 2026 system: ICE + turbo + MGU-K + ES (no MGU-H)." },
  { term: "Push Lap", definition: "Fast effort lap in practice/quali." },
  { term: "Racing Line", definition: "Optimal path minimizing time." },
  { term: "Recharge", definition: "Harvesting energy via braking/lift-off/coasting." },
  { term: "Rear Wing (RW)", definition: "Trailing aero, movable in Active Aero." },
  { term: "Safety Car", definition: "Leads field under caution to slow/bunch cars." },
  { term: "Setup", definition: "Car configuration (aero, suspension, etc.)." },
  { term: "Simplified Hybrid", definition: "2026 PU without MGU-H (cost/weight cut, some lag return)." },
  { term: "Slipstream (Drafting)", definition: "Following to reduce drag/gain speed." },
  { term: "Smaller and Lighter Cars", definition: "2026: 3400mm wheelbase, 1900mm width, narrower tyres, 770kg." },
  { term: "Straight Mode", definition: "Active Aero low-drag setup for straights." },
  { term: "Strategy", definition: "Tyre/pit/energy/fuel planning." },
  { term: "Torque", definition: "Engine rotational force (acceleration feel)." },
  { term: "Turbo Lag", definition: "Power delay post-throttle (mild return in 2026)." },
  { term: "Tyres", definition: "Pirelli; narrower fronts/rears in 2026 for drag/weight reduction." },
  { term: "Undercut", definition: "Earlier pit stop to leapfrog rival on fresh tyres." },
  { term: "Understeer", definition: "Front pushes wide in corner (tight handling)." },
  { term: "Virtual Safety Car (VSC)", definition: "Slow to delta time under caution (no bunching)." },
  { term: "Wheelbase", definition: "Front-to-rear axle distance (shortened in 2026)." },
];

const EXPLAINED_TOPICS = [
  {
    id: 'tyres',
    title: 'Tyres',
    icon: '🏎️',
    summary: 'The only contact between car and track - crucial for grip and strategy.',
    content: [
      {
        subtitle: 'Compound Types',
        text: 'Pirelli supplies all F1 tyres with five dry compounds (C1-C5). C1 is the hardest and most durable, C5 is the softest and fastest but wears quickly. Three compounds are selected per race weekend, color-coded as Hard (white), Medium (yellow), and Soft (red).'
      },
      {
        subtitle: 'Wet Weather Tyres',
        text: 'Intermediate (green) tyres for damp conditions clear about 30 litres per second. Full Wet (blue) tyres for heavy rain clear about 65 litres per second with deeper treads.'
      },
      {
        subtitle: '2026 Changes',
        text: 'Front tyres narrowed from 305mm to 285mm, rears from 405mm to 365mm. This reduces drag and weight while promoting closer racing.'
      },
      {
        subtitle: 'Tyre Management',
        text: 'Blistering occurs when overheating causes bubbles on the surface. Graining happens when the rubber tears into chunks. Both significantly reduce grip and lap times.'
      }
    ]
  },
  {
    id: 'flags',
    title: 'Flags',
    icon: '🚩',
    summary: 'Visual communication system between race control and drivers.',
    content: [
      {
        subtitle: 'Green Flag',
        text: 'Track is clear - racing can proceed normally. Shown after a hazard has been cleared.'
      },
      {
        subtitle: 'Yellow Flag',
        text: 'Single yellow: Danger ahead, slow down, no overtaking. Double yellow: Great danger, be prepared to stop, no overtaking.'
      },
      {
        subtitle: 'Red Flag',
        text: 'Session stopped immediately. All cars must slow down significantly and return to pit lane. Used for serious accidents or dangerous conditions.'
      },
      {
        subtitle: 'Blue Flag',
        text: 'Shown to lapped cars when faster traffic approaches. Driver must let the faster car through within 3 blue flags or face a penalty.'
      },
      {
        subtitle: 'Black Flag',
        text: 'Driver must return to pits immediately and is excluded from the session. Shown for serious rule violations.'
      },
      {
        subtitle: 'Black & White Flag',
        text: 'Warning for unsportsmanlike behavior. Essentially a final warning before a black flag.'
      },
      {
        subtitle: 'Black & Orange Flag',
        text: 'Car has mechanical problem and must pit for inspection. Driver number displayed alongside.'
      },
      {
        subtitle: 'Chequered Flag',
        text: 'Session complete. In a race, the leader crossing the line under chequered flag wins.'
      }
    ]
  },
  {
    id: 'penalties',
    title: 'Penalties',
    icon: '⚠️',
    summary: 'Punishments for rule violations during sessions and races.',
    content: [
      {
        subtitle: 'Time Penalties',
        text: '5 seconds: Minor infringements (track limits, unsafe release). Added during pit stop or to race time. 10 seconds: More serious violations. Can also be a stop-and-go without the 10s wait.'
      },
      {
        subtitle: 'Drive-Through Penalty',
        text: 'Driver must enter pit lane and drive through without stopping. Costs approximately 20-25 seconds depending on circuit.'
      },
      {
        subtitle: 'Stop-and-Go Penalty',
        text: 'Driver must stop in their pit box for a specified time (usually 10 seconds) without any work being done on the car.'
      },
      {
        subtitle: 'Grid Penalties',
        text: '3, 5, or 10 place grid drops for next race. Can be for causing collisions, impeding in qualifying, or power unit component changes.'
      },
      {
        subtitle: 'Disqualification',
        text: 'Complete exclusion from session or race results. Applied for serious technical infringements or dangerous driving.'
      },
      {
        subtitle: 'Penalty Points',
        text: 'Drivers accumulate points on their Super Licence for infringements. 12 points within 12 months = 1 race ban. Points expire after 12 months.'
      }
    ]
  },
  {
    id: 'qualifying',
    title: 'Qualifying',
    icon: '⏱️',
    summary: 'Saturday session determining starting grid positions.',
    content: [
      {
        subtitle: 'Q1 (18 minutes)',
        text: 'All 20 drivers compete. Slowest 5 drivers are eliminated and start P16-P20. Track evolution means late laps are usually faster.'
      },
      {
        subtitle: 'Q2 (15 minutes)',
        text: 'Remaining 15 drivers compete. Slowest 5 are eliminated and start P11-P15. Top 10 must start race on the tyre compound used for their fastest Q2 lap.'
      },
      {
        subtitle: 'Q3 (12 minutes)',
        text: 'Final shootout for pole position. Top 10 drivers fight for front row positions. Often comes down to thousandths of a second.'
      },
      {
        subtitle: 'Sprint Qualifying',
        text: 'On sprint weekends, a short race (about 100km) determines Sunday grid. Traditional qualifying sets sprint grid, sprint result sets race grid.'
      }
    ]
  },
  {
    id: 'pit-stops',
    title: 'Pit Stops',
    icon: '🔧',
    summary: 'Lightning-fast tyre changes that can make or break a race.',
    content: [
      {
        subtitle: 'The Crew',
        text: 'Around 20 people work on a pit stop: 3 per wheel (gunman, off, on), front/rear jackmen, stabilizers, and lollipop/traffic light operator.'
      },
      {
        subtitle: 'Speed Records',
        text: 'Top teams complete stops in under 2 seconds. The record is 1.80 seconds (Red Bull, 2019). Hundredths of seconds can decide race positions.'
      },
      {
        subtitle: 'Strategy',
        text: 'Undercut: Pitting early to get fresh tyres and gain time. Overcut: Staying out longer hoping rivals wear their tyres. Teams monitor gaps constantly.'
      },
      {
        subtitle: 'Unsafe Release',
        text: 'If a car is released into traffic or with loose wheel, penalties apply. Can be 5 seconds or worse if dangerous.'
      }
    ]
  },
  {
    id: 'drs-overtake',
    title: 'Overtaking & DRS',
    icon: '🏁',
    summary: 'How drivers pass each other - evolving for 2026.',
    content: [
      {
        subtitle: 'Traditional DRS (Pre-2026)',
        text: 'Drag Reduction System opened rear wing flap when within 1 second of car ahead in designated zones. Gave ~10-15 km/h speed boost.'
      },
      {
        subtitle: '2026 Overtake Mode',
        text: 'Replaces DRS with electronic boost. When within 1 second at detection points, trailing car gets extra +0.5MJ energy deployment for more power.'
      },
      {
        subtitle: 'Active Aero',
        text: 'Front and rear wings adjust between Corner Mode (high downforce) and Straight Mode (low drag). All cars can use this anywhere, not just for overtaking.'
      },
      {
        subtitle: 'Slipstream',
        text: 'Following in another car\'s wake reduces air resistance, allowing higher straight-line speed. Works best on long straights.'
      }
    ]
  },
  {
    id: 'points',
    title: 'Points System',
    icon: '🏆',
    summary: 'How championships are won and lost.',
    content: [
      {
        subtitle: 'Race Points',
        text: '1st: 25 pts, 2nd: 18, 3rd: 15, 4th: 12, 5th: 10, 6th: 8, 7th: 6, 8th: 4, 9th: 2, 10th: 1.'
      },
      {
        subtitle: 'Fastest Lap',
        text: '+1 bonus point for driver setting fastest race lap, but only if finishing in top 10.'
      },
      {
        subtitle: 'Sprint Points',
        text: '1st: 8 pts, 2nd: 7, 3rd: 6, 4th: 5, 5th: 4, 6th: 3, 7th: 2, 8th: 1. No fastest lap bonus in sprints.'
      },
      {
        subtitle: 'Championships',
        text: 'Drivers\' Championship: Individual standings. Constructors\' Championship: Both team cars\' points combined. Both titles carry prestige and prize money.'
      }
    ]
  },
  {
    id: 'safety',
    title: 'Safety Systems',
    icon: '🛡️',
    summary: 'Protecting drivers at 300+ km/h.',
    content: [
      {
        subtitle: 'Halo',
        text: 'Titanium structure above cockpit introduced 2018. Withstands 12 tonnes of force. Has saved multiple drivers from serious injury.'
      },
      {
        subtitle: 'Safety Car',
        text: 'Mercedes-AMG GT leads field under caution after serious incidents. Bunches field together while marshals work. No overtaking allowed.'
      },
      {
        subtitle: 'Virtual Safety Car (VSC)',
        text: 'All cars slow to a delta time (30% slower) but maintain positions without bunching. Used for minor incidents requiring caution.'
      },
      {
        subtitle: 'HANS Device',
        text: 'Head and Neck Support device prevents basilar skull fractures in impacts. Mandatory for all drivers.'
      },
      {
        subtitle: 'Survival Cell',
        text: 'Carbon fiber monocoque designed to remain intact even in severe crashes. Must pass FIA crash tests.'
      }
    ]
  }
];

export default function EducationPage() {
  const [activeTab, setActiveTab] = useState('glossary');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTopic, setExpandedTopic] = useState(null);

  const filteredGlossary = GLOSSARY_TERMS.filter(item =>
    item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredExplained = EXPLAINED_TOPICS.filter(topic =>
    topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    topic.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black font-sans" data-testid="education-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#CCFF00] rounded-sm flex items-center justify-center">
              <Zap size={20} className="text-black fill-black" />
            </div>
            <span className="font-display font-bold text-2xl tracking-tighter">BORMULA</span>
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-[#CCFF00] font-bold uppercase text-xs tracking-widest transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#CCFF00]/10 blur-[150px] -z-10"></div>
        <div className="max-w-7xl mx-auto">
          {/* Header - centered on mobile */}
          <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left gap-4 mb-6">
            <GraduationCap className="text-[#CCFF00] w-12 h-12 md:w-12 md:h-12" />
            <div>
              <h1 className="text-4xl md:text-7xl font-display font-bold uppercase tracking-tighter">
                F1 <span className="text-[#CCFF00]">Education</span>
              </h1>
              <p className="text-white/40 text-base md:text-lg mt-2">Learn the language of Formula 1</p>
            </div>
          </div>

          {/* Tab Switcher - centered on mobile */}
          <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-4 mt-8 md:mt-12">
            <button
              onClick={() => { setActiveTab('glossary'); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-5 md:px-8 py-3 md:py-4 rounded-2xl text-xs md:text-sm font-black uppercase tracking-widest transition-all ${
                activeTab === 'glossary' 
                  ? 'bg-[#CCFF00] text-black' 
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              data-testid="tab-glossary"
            >
              <BookOpen size={16} /> Glossary
            </button>
            <button
              onClick={() => { setActiveTab('explained'); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-5 md:px-8 py-3 md:py-4 rounded-2xl text-xs md:text-sm font-black uppercase tracking-widest transition-all ${
                activeTab === 'explained' 
                  ? 'bg-[#CCFF00] text-black' 
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              data-testid="tab-explained"
            >
              <GraduationCap size={16} /> Explained
            </button>
          </div>

          {/* Search - centered on mobile */}
          <div className="relative mt-6 md:mt-8 max-w-xl mx-auto md:mx-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              type="text"
              placeholder={activeTab === 'glossary' ? "Search terms..." : "Search topics..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-[#111111] border-white/10 text-white placeholder:text-white/40 rounded-xl h-12 w-full"
              data-testid="search-education"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          
          {/* Glossary Tab */}
          {activeTab === 'glossary' && (
            <div className="space-y-2" data-testid="glossary-content">
              <div className="mb-8">
                <p className="text-white/40 text-sm">
                  {filteredGlossary.length} terms • Updated for 2026 regulations
                </p>
              </div>
              
              {filteredGlossary.map((item, index) => (
                <div
                  key={index}
                  className="group p-5 bg-[#111111] border border-white/5 rounded-xl hover:border-[#CCFF00]/30 transition-colors"
                  data-testid={`glossary-term-${index}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-6">
                    <h3 className="font-display font-bold text-lg text-[#CCFF00] uppercase min-w-[280px]">
                      {item.term}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {item.definition}
                    </p>
                  </div>
                </div>
              ))}

              {filteredGlossary.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-white/40">No terms found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          )}

          {/* Explained Tab */}
          {activeTab === 'explained' && (
            <div className="space-y-4" data-testid="explained-content">
              <div className="mb-8">
                <p className="text-white/40 text-sm">
                  {filteredExplained.length} topics covering F1 fundamentals
                </p>
              </div>

              {filteredExplained.map((topic) => (
                <div
                  key={topic.id}
                  className="bg-[#111111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#CCFF00]/30 transition-colors"
                  data-testid={`explained-topic-${topic.id}`}
                >
                  {/* Topic Header */}
                  <button
                    onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
                    className="w-full p-6 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{topic.icon}</span>
                      <div>
                        <h3 className="font-display font-bold text-2xl uppercase group-hover:text-[#CCFF00] transition-colors">
                          {topic.title}
                        </h3>
                        <p className="text-white/50 text-sm mt-1">{topic.summary}</p>
                      </div>
                    </div>
                    <ChevronDown 
                      className={`w-6 h-6 text-[#CCFF00] transition-transform ${
                        expandedTopic === topic.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Topic Content */}
                  {expandedTopic === topic.id && (
                    <div className="px-6 pb-6 border-t border-white/5">
                      <div className="pt-6 space-y-6">
                        {topic.content.map((section, idx) => (
                          <div key={idx} className="pl-4 border-l-2 border-[#CCFF00]/30">
                            <h4 className="font-bold text-[#CCFF00] uppercase text-sm tracking-wider mb-2">
                              {section.subtitle}
                            </h4>
                            <p className="text-white/70 leading-relaxed">
                              {section.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {filteredExplained.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-white/40">No topics found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#CCFF00] rounded-sm flex items-center justify-center">
              <Zap size={14} className="text-black fill-black" />
            </div>
            <span className="font-display font-bold text-xl tracking-tighter uppercase">Bormula</span>
          </div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20">
            &copy; 2024 BORMULA MEDIA GROUP
          </div>
        </div>
      </footer>
    </div>
  );
}
